import { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useDemoData } from '@mui/x-data-grid-generator';
import {
  Box, Tabs, Tab, Typography, TextField, Button, Paper, Container, Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
const VISIBLE_FIELDS = ['name', 'rating', 'country', 'dateCreated', 'isAdmin'];

// Styled components
const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(2, 0),
  backgroundColor: theme.palette.grey[100],
}));

const ScrollableTableContainer = styled('div')({
  width: '100%',
  overflowX: 'auto',
  '& .MuiDataGrid-root': {
    minWidth: '800px',
  },
  '& .MuiDataGrid-row': {
    minHeight: '32px !important',
    maxHeight: '32px !important',
  },
  '& .MuiDataGrid-cell': {
    padding: '4px 8px',
    lineHeight: '1.2 !important',
  },
  '& .MuiDataGrid-columnHeaders': {
    minHeight: '36px !important',
    maxHeight: '36px !important',
  },
});

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 1 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

const AccountsDetails = () => {
  const [tabValue, setTabValue] = useState(0);
  const [commonFilter, setCommonFilter] = useState({
    property: '',
    unit: '',
    date: '',
    type: '',
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCommonFilterChange = (e) => {
    const { name, value } = e.target;
    setCommonFilter(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyCommonFilters = () => {
    console.log('Applying common filters:', commonFilter);
    // Implement your actual filtering logic here
  };

  const resetCommonFilters = () => {
    setCommonFilter({
      property: '',
      unit: '',
      date: '',
      type: '',
    });
  };

  // Different data sets for each tab
  const { data: enquiryData, loading: enquiryLoading } = useDemoData({
    dataSet: 'Employee',
    visibleFields: [...VISIBLE_FIELDS, 'email', 'phone', 'address', 'department', 'joinDate'],
    rowLength: 100,
  });

  const { data: adviceData, loading: adviceLoading } = useDemoData({
    dataSet: 'Commodity',
    visibleFields: [...VISIBLE_FIELDS, 'price', 'currency', 'inventory', 'category', 'supplier'],
    rowLength: 80,
  });

  const { data: bookingData, loading: bookingLoading } = useDemoData({
    dataSet: 'Customer',
    visibleFields: [...VISIBLE_FIELDS, 'company', 'position', 'status', 'lastPurchase', 'loyaltyPoints'],
    rowLength: 60,
  });

  const { data: receiptData, loading: receiptLoading } = useDemoData({
    dataSet: 'Employee',
    visibleFields: [...VISIBLE_FIELDS, 'salary', 'bonus', 'manager', 'projects', 'skills'],
    rowLength: 75,
  });

  const { data: refundData, loading: refundLoading } = useDemoData({
    dataSet: 'Commodity',
    visibleFields: [...VISIBLE_FIELDS, 'discount', 'tax', 'shippingCost', 'weight', 'dimensions'],
    rowLength: 50,
  });

  // Filter models for each tab
  const [filterModels, setFilterModels] = useState({
    0: { items: [] },
    1: { items: [] },
    2: { items: [] },
    3: { items: [] },
    4: { items: [] },
  });

  const handleFilterModelChange = (newFilterModel, tabIndex) => {
    setFilterModels(prev => ({
      ...prev,
      [tabIndex]: newFilterModel
    }));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }} >
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="account tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Enquiry" {...a11yProps(0)} />
          <Tab label="Booking" {...a11yProps(2)} />
          <Tab label="Advice" {...a11yProps(1)} />
          <Tab label="Receipt" {...a11yProps(3)} />
          <Tab label="Refund" {...a11yProps(4)} />
        </Tabs>
      </Box>

      {/* Common Filter Section */}
      <FilterSection elevation={3}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            label="Property"
            name="property"
            value={commonFilter.property}
            onChange={handleCommonFilterChange}
            size="small"
          />
          <TextField
            label="From Date"
            name="date"
            type="date"
            value={commonFilter.date}
            onChange={handleCommonFilterChange}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To Date"
            name="date"
            type="date"
            value={commonFilter.date}
            onChange={handleCommonFilterChange}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Resident"
            name="type"
            value={commonFilter.type}
            onChange={handleCommonFilterChange}
            size="small"
          />
          <TextField
            label="Unit"
            name="unit"
            value={commonFilter.unit}
            onChange={handleCommonFilterChange}
            size="small"
          />
          <Button
            variant="contained"
            onClick={applyCommonFilters}
            sx={{ ml: 2 }}
          >
            Apply
          </Button>
          <Button
            variant="outlined"
            onClick={resetCommonFilters}
          >
            Reset
          </Button>
        </Stack>
      </FilterSection>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <ScrollableTableContainer>
          <div style={{ height: 500 }}>
            <DataGrid
              {...enquiryData}
              loading={enquiryLoading}
              showToolbar
              filterModel={filterModels[0]}
              onFilterModelChange={(newModel) => handleFilterModelChange(newModel, 0)}
              density="compact"
            />
          </div>
        </ScrollableTableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <ScrollableTableContainer>
          <div style={{ height: 500 }}>
            <DataGrid
              {...adviceData}
              loading={adviceLoading}
              showToolbar
              filterModel={filterModels[1]}
              onFilterModelChange={(newModel) => handleFilterModelChange(newModel, 1)}
              density="compact"
            />
          </div>
        </ScrollableTableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <ScrollableTableContainer>
          <div style={{ height: 500 }}>
            <DataGrid
              {...bookingData}
              loading={bookingLoading}
              showToolbar
              filterModel={filterModels[2]}
              onFilterModelChange={(newModel) => handleFilterModelChange(newModel, 2)}
              density="compact"
            />
          </div>
        </ScrollableTableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <ScrollableTableContainer>
          <div style={{ height: 500 }}>
            <DataGrid
              {...receiptData}
              loading={receiptLoading}
              showToolbar
              filterModel={filterModels[3]}
              onFilterModelChange={(newModel) => handleFilterModelChange(newModel, 3)}
              density="compact"
            />
          </div>
        </ScrollableTableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <ScrollableTableContainer>
          <div style={{ height: 500 }}>
            <DataGrid
              {...refundData}
              loading={refundLoading}
              showToolbar
              filterModel={filterModels[4]}
              onFilterModelChange={(newModel) => handleFilterModelChange(newModel, 4)}
              density="compact"
            />
          </div>
        </ScrollableTableContainer>
      </TabPanel>
    </Container>
  );
};

export default AccountsDetails;